/**
 * FeatureFlagsService — thin Angular-signal wrapper around
 * `posthog.isFeatureEnabled()` and `posthog.onFeatureFlags()`.
 *
 * RESPONSIBILITY: Expose every active PostHog flag as a reactive signal so
 * components and computed values can react to flag changes without
 * subscribing to a callback manually. PostHog evaluates flags client-side
 * once the SDK is initialised and re-evaluates whenever person properties
 * or super-properties change (e.g. after `identify` runs).
 *
 * SIGNALS:
 *   ready  — true once PostHog has evaluated flags for the current viewer.
 *            Gate UI on this to avoid flicker between "flag is off" (the
 *            default before evaluation) and the real evaluated value.
 *   flags  — readonly map of flagKey → variantValue (string | boolean).
 *            Subscribers see the latest evaluation; nothing else to wire.
 *
 * USED BY: Anywhere a component or route needs to gate behaviour on a
 * PostHog feature flag. Example:
 *
 *   protected readonly showNewPrompt = computed(() =>
 *     this.featureFlags.flagEnabled('ai-prompts-v2'),
 *   );
 *
 * BACKEND CONTRACT: None. Flags are managed in PostHog UI (or via the
 * MCP — see WS3.3). Flag definitions documented in docs/ANALYTICS.md.
 */
import { Injectable, computed, inject, signal } from '@angular/core';
import { PosthogService } from './posthog.service';

@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly posthog = inject(PosthogService);

  private readonly _ready = signal(false);
  private readonly _flags = signal<Record<string, string | boolean>>({});

  readonly ready = this._ready.asReadonly();
  readonly flags = this._flags.asReadonly();

  constructor() {
    // posthog.onFeatureFlags fires once flags are evaluated for the current
    // person, and again every time the evaluation changes (e.g. after the
    // user is identified, after person properties update, after the SDK
    // refreshes flags). Subscribe once; the signal updates do the fan-out.
    try {
      this.posthog.posthog.onFeatureFlags?.(
        (_keys: string[], variants: Record<string, string | boolean>) => {
          this._flags.set(variants);
          this._ready.set(true);
        },
      );
    } catch (err) {
      console.warn('[FeatureFlags] onFeatureFlags subscribe failed:', err);
    }
  }

  /** True when the flag is on (boolean true OR any non-default variant). */
  flagEnabled(key: string): boolean {
    const value = this._flags()[key];
    return value === true || (typeof value === 'string' && value !== '');
  }

  /** Variant value when the flag is multivariate. Returns the variant key
   *  (e.g. "control" / "treatment") or null when off / not loaded. */
  flagVariant(key: string): string | null {
    const value = this._flags()[key];
    return typeof value === 'string' ? value : null;
  }

  /** Computed convenience: returns a signal that re-renders when the flag
   *  changes. Use in templates or computed() chains. */
  watch(key: string) {
    return computed(() => this.flagEnabled(key));
  }
}
